'use client'

import React from 'react'
import Link from 'next/link'
import { ActionIcon, Box, Flex } from '@mantine/core'
import { IconArrowLeft, IconX } from '@tabler/icons-react'
import { AnimatePresence, motion } from 'framer-motion'
import { GmCrossIcon } from '@/components/Icons/GmCrossIcon'

const SWAP_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1]

const iconSwapVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir > 0 ? 8 : -8 }),
    center: { opacity: 1, x: 0 },
    exit: (dir: number) => ({ opacity: 0, x: dir > 0 ? -8 : 8 }),
}

interface DrawerHeaderProps {
    showBack: boolean
    direction: number
    onBack: () => void
    onClose: () => void
    onLogoClick: () => void
}

export function DrawerHeader({ showBack, direction, onBack, onClose, onLogoClick }: DrawerHeaderProps) {
    return (
        <Flex justify="space-between" align="center" px={24} py={20}>
            <Box style={{ position: 'relative', width: 44, height: 44, display: 'flex', alignItems: 'center' }}>
                <AnimatePresence mode="wait" initial={false} custom={direction}>
                    {showBack ? (
                        <motion.div
                            key="back"
                            custom={direction}
                            variants={iconSwapVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.18, ease: SWAP_EASE }}
                            style={{ position: 'absolute', inset: 0 }}
                        >
                            <CircleIconButton onClick={onBack} ariaLabel="Back">
                                <IconArrowLeft size={18} />
                            </CircleIconButton>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="cross"
                            custom={direction}
                            variants={iconSwapVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.18, ease: SWAP_EASE }}
                            style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}
                        >
                            <Link
                                href="/"
                                onClick={onLogoClick}
                                aria-label="General Medicine home"
                                style={{ outline: 'none', display: 'inline-flex' }}
                            >
                                <GmCrossIcon width={32} height={32} fill="var(--gm-blue, #2d1aff)" />
                            </Link>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>
            <CircleIconButton onClick={onClose} ariaLabel="Close menu">
                <IconX size={18} />
            </CircleIconButton>
        </Flex>
    )
}

function CircleIconButton({
    onClick,
    ariaLabel,
    children,
}: {
    onClick: () => void
    ariaLabel: string
    children: React.ReactNode
}) {
    return (
        <ActionIcon
            variant="default"
            radius="xl"
            size={44}
            onClick={onClick}
            aria-label={ariaLabel}
            c="var(--gm-fg-primary-black, #1f1e1d)"
            bd="1px solid var(--Foreground-fg-outline-primary, #D1D0CF)"
            __vars={{
                '--ai-bg': 'transparent',
                '--ai-hover': 'var(--gm-bg-highlight-cont, rgba(45, 26, 255, 0.1))',
            }}
        >
            {children}
        </ActionIcon>
    )
}
